// client/src/components/BookRater.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import TinderCard from 'react-tinder-card';
import {
    Box, Typography, CircularProgress, Button, IconButton, Paper, Alert,
    AppBar, Toolbar, Stack, useMediaQuery, Snackbar, Avatar
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // Behalten
import CancelIcon from '@mui/icons-material/Cancel'; // Verwerfen
import ReplayIcon from '@mui/icons-material/Replay'; // Neu laden
import ArrowBackIcon from '@mui/icons-material/ArrowBack'; // Zurück
import UndoIcon from '@mui/icons-material/Undo'; // Undo
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'; // Auswahl Lesestatus
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked'; // Auswahl Lesestatus (alternativ)

import { fetchUnratedBooksAPI, rateBookAPI, deleteRatingAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import './BookRater.css'; // CSS für Karten-Styling und Positionierung

const BookRater = () => {
    // --- State ---
    const [books, setBooks] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastAction, setLastAction] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    // --- Änderung: Initialen Lesestatus auf null setzen ---
    const [readingDecision, setReadingDecision] = useState(null); // Vorher: 'wants_to_read'
    // --- Ende Änderung ---

    // --- Hooks ---
    const { handleApiError } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // --- Refs ---
    // Refs für jede Karte, um programmatisches Swipen/Restoren zu ermöglichen
    const childRefs = useMemo(() =>
        Array(books.length).fill(0).map(() => React.createRef()),
    [books.length]);

    // --- Computed State ---
    const currentBook = books[currentIndex];
    const canRate = !isLoading && !isProcessing && currentBook;
    const canUndo = !isLoading && !isProcessing && !!lastAction;

    // --- Snackbar Helper ---
    const showSnackbar = useCallback((message, severity = 'info') => {
        setSnackbar({ open: true, message, severity });
    }, []);

    const handleSnackbarClose = useCallback((event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar(prev => ({ ...prev, open: false }));
    }, []);

    // --- Daten Laden ---
    const loadBooks = useCallback(async (reset = true) => {
        setIsLoading(true);
        setError(null);
        if (reset) {
            setLastAction(null); // Undo zurücksetzen
            setCurrentIndex(0); // Index zurücksetzen
        }
        try {
            const fetchedBooks = await fetchUnratedBooksAPI(20); // Lade bis zu 20 Bücher
            if (fetchedBooks && fetchedBooks.length > 0) {
                setBooks(fetchedBooks);
                setCurrentIndex(fetchedBooks.length - 1); // Starte mit dem obersten Buch (letztes im Array)
                console.log(`[loadBooks] Loaded ${fetchedBooks.length} books. Current index: ${fetchedBooks.length - 1}`);
            } else {
                setBooks([]);
                setCurrentIndex(-1); // Kein Buch vorhanden
                console.log("[loadBooks] No unrated books found.");
            }
        } catch (err) {
            handleApiError(err);
            setError(`Fehler beim Laden: ${err.message}`);
            showSnackbar(`Fehler beim Laden: ${err.message}`, 'error');
            setBooks([]);
            setCurrentIndex(-1);
        } finally {
            setIsLoading(false);
        }
    }, [handleApiError, showSnackbar]);

    // Initiales Laden
    useEffect(() => {
        loadBooks();
    }, [loadBooks]);

    // --- Aktionen ---

    // Wird aufgerufen, wenn eine Karte den Bildschirm verlässt
    const handleCardLeftScreen = useCallback((bookId, keepStatus) => {
        console.log(`[handleCardLeftScreen] Book ${bookId} left screen (${keepStatus}). Current index: ${currentIndex}`);
        setCurrentIndex(prevIndex => prevIndex - 1);
        // --- Änderung: Lesestatus auf null zurücksetzen für die nächste Karte ---
        setReadingDecision(null); // Vorher: 'wants_to_read'
        // --- Ende Änderung ---
    }, [currentIndex]); // Abhängigkeit von currentIndex bleibt

    // Führt die Bewertungsaktion aus (API + State für Undo)
    const handleSwipeAction = useCallback(async (book, keepStatus) => {
        if (isProcessing) return;
        setIsProcessing(true);

        const readingStatusToSend = readingDecision; // Dieser bleibt für die API
        console.log(`[handleSwipeAction] Rating book ${book._id} as ${keepStatus}, reading: ${readingStatusToSend} (explicitly chosen: ${readingDecision})`);

        // --- KORREKTUR: Speichere den *tatsächlichen* readingDecision State für Undo ---
        // Statt readingStatusToSend verwenden wir readingDecision (kann null sein)
        setLastAction({ book, readingStatus: readingDecision, keepStatus });
        // --- ENDE KORREKTUR ---

        try {
            // Verwende weiterhin readingStatusToSend für die API
            await rateBookAPI(book._id, readingStatusToSend, keepStatus);
            showSnackbar(`"${book.title}" als "${keepStatus === 'keep' ? 'Behalten' : 'Kann weg'}" markiert.`, 'success');
        } catch (err) {
            handleApiError(err);
            setError(`Fehler beim Bewerten: ${err.message}`);
            showSnackbar(`Fehler: ${err.message}`, 'error');
            setLastAction(null);
        } finally {
            setTimeout(() => setIsProcessing(false), 500);
        }
        // Wichtig: readingDecision selbst wird erst in handleCardLeftScreen zurückgesetzt!
    }, [isProcessing, readingDecision, handleApiError, showSnackbar]); // readingDecision als Abhängigkeit hinzugefügt

    // Wird von TinderCard aufgerufen, wenn ein Swipe *beginnt* oder *passiert*
    const swiped = useCallback((direction, book) => {
        console.log(`[swiped] Manual swipe detected: ${direction} for ${book.title}`);
        const keepStatus = direction === 'right' ? 'keep' : 'discard';
        handleSwipeAction(book, keepStatus);
    }, [handleSwipeAction]);

    // Programmatischer Swipe über Buttons
    const triggerSwipe = useCallback(async (direction) => {
        if (!canRate || !currentBook) return;

        const keepStatus = direction === 'right' ? 'keep' : 'discard';
        const bookToSwipe = currentBook;

        // Führe zuerst die Swipe-Aktion (API + Undo-State) aus
        // handleSwipeAction verwendet jetzt den aktuellen readingDecision oder den Default
        await handleSwipeAction(bookToSwipe, keepStatus);

        // Dann löse die Animation aus
        const ref = childRefs[currentIndex];
        if (ref && ref.current) {
            console.log(`[triggerSwipe] Triggering programmatic swipe ${direction} for ${bookToSwipe.title}`);
            try {
                await ref.current.swipe(direction);
            } catch (animError) {
                console.error(`[triggerSwipe] Animation error: ${animError}`);
                handleCardLeftScreen(bookToSwipe._id, keepStatus);
            }
        } else {
            console.warn(`[triggerSwipe] No ref found for index ${currentIndex}. Manually updating index.`);
            handleCardLeftScreen(bookToSwipe._id, keepStatus);
        }
    }, [canRate, currentBook, currentIndex, childRefs, handleSwipeAction, handleCardLeftScreen]);

    // Undo-Aktion
    const handleUndo = useCallback(async () => {
        if (!canUndo) return;

        setIsProcessing(true);
        const actionToUndo = lastAction;
        console.log(`[handleUndo] Undoing action for book ${actionToUndo.book.title}`);

        try {
            await deleteRatingAPI(actionToUndo.book._id);

            const newIndex = currentIndex + 1;
            setCurrentIndex(newIndex);

            // --- KORREKTUR: Setze den Lesestatus zurück, der *vor* der Aktion galt ---
            // Da lastAction.readingStatus jetzt den tatsächlichen vorherigen State (ggf. null) enthält,
            // wird hier der korrekte Zustand wiederhergestellt.
            setReadingDecision(actionToUndo.readingStatus);
            // --- ENDE KORREKTUR ---

            setLastAction(null);

            setTimeout(() => {
                const ref = childRefs[newIndex];
                if (ref && ref.current) {
                    console.log(`[handleUndo] Triggering restoreCard animation for ${actionToUndo.book.title}`);
                    ref.current.restoreCard();
                } else {
                    console.warn(`[handleUndo] No ref found for index ${newIndex} during restore.`);
                }
                setIsProcessing(false);
            }, 50);

            showSnackbar(`Letzte Bewertung für "${actionToUndo.book.title}" rückgängig gemacht.`, 'info');

        } catch (err) {
            handleApiError(err);
            setError(`Fehler beim Rückgängigmachen: ${err.message}`);
            showSnackbar(`Fehler beim Rückgängigmachen: ${err.message}`, 'error');
            setIsProcessing(false);
        }
    }, [canUndo, lastAction, currentIndex, childRefs, handleApiError, showSnackbar]);

    // --- Rendering ---

    const renderCardContent = (book) => {
        const coverUrl = book.cachedCoverUrl || book.thumbnail?.replace(/^http:\/\//, 'https://');
        const showRealCover = coverUrl && !coverUrl.endsWith('generic-book-cover.png');

        return (
            // Diese Box ist der sichtbare Inhalt der Karte
            <Box sx={{
                width: '100%', height: '100%', borderRadius: '20px', overflow: 'hidden',
                boxShadow: 1, // Subtiler Schatten
                bgcolor: 'background.paper', display: 'flex', flexDirection: 'column',
                position: 'relative',
            }}>
                {/* Bildbereich */}
                <Box sx={{
                    height: '70%', // Angepasste Höhe
                    width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center',
                    overflow: 'hidden', bgcolor: 'grey.200', position: 'relative'
                }}>
                    {showRealCover ? (
                        <img
                            src={coverUrl}
                            alt={`Cover von ${book.title}`}
                            style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                    ) : (
                        <Avatar sx={{ width: 100, height: 150, bgcolor: 'grey.400' }} variant="rounded">
                            <Typography variant="caption" sx={{ textAlign: 'center', p: 1 }}>Kein Cover</Typography>
                        </Avatar>
                    )}
                </Box>
                {/* Textbereich */}
                <Box sx={{
                    p: 1.5, // Angepasstes Padding
                    flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center'
                 }}>
                    <Typography variant="h6" component="div" noWrap sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1.1rem' }}>
                        {book.title || 'Unbekannter Titel'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ textAlign: 'center', fontSize: '0.9rem' }}>
                        {Array.isArray(book.authors) ? book.authors.join(', ') : (book.authors || 'Unbekannte Autoren')}
                    </Typography>
                </Box>
            </Box>
        );
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', bgcolor: 'grey.100' }}>
            {/* AppBar */}
            <AppBar position="static" color="transparent" elevation={0}>
                <Toolbar sx={{ justifyContent: 'space-between' }}>
                    <IconButton edge="start" color="inherit" onClick={() => navigate('/booklist')} aria-label="Zurück" disabled={isProcessing}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" color="text.primary" component="div">
                        Bücher bewerten
                    </Typography>
                    <IconButton color="inherit" onClick={handleUndo} disabled={!canUndo} aria-label="Rückgängig">
                        {isProcessing && lastAction ? <CircularProgress size={24} color="inherit" /> : <UndoIcon />}
                    </IconButton>
                </Toolbar>
            </AppBar>

            {/* Info/Error Bereich */}
            <Box sx={{ p: 1, textAlign: 'center', minHeight: '48px' }}>
                 {isLoading && <CircularProgress size={20} />}
                 {error && <Alert severity="error" sx={{ justifyContent: 'center', py: 0.5, fontSize: '0.8rem' }}>{error}</Alert>}
            </Box>

            {/* Kartenstapel */}
            <Box sx={{
                flexGrow: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
                my: 1,
                // Kein px mehr hier
            }}>
                {books.length > 0 && currentIndex >= 0 ? (
                    // Dieser div erhält die Größe aus BookRater.css
                    <div className="cardContainer">
                        {books.map((book, index) => (
                            <TinderCard
                                ref={childRefs[index]}
                                key={book._id}
                                onSwipe={(dir) => swiped(dir, book)}
                                onCardLeftScreen={() => handleCardLeftScreen(book._id, lastAction?.book?._id === book._id ? lastAction.keepStatus : 'unknown')}
                                preventSwipe={['up', 'down']}
                                className="swipe"
                                swipeRequirementType="position"
                                swipeThreshold={100}
                                disabled={isProcessing || index !== currentIndex}
                            >
                                {renderCardContent(book)}
                            </TinderCard>
                        ))}
                    </div>
                ) : (
                    // Nachricht, wenn keine Bücher mehr da sind oder beim initialen Laden
                    <Paper sx={{ p: 3, textAlign: 'center', m: 2 }}>
                        {isLoading ? <CircularProgress /> : (
                            <>
                                <Typography variant="h6">🎉 Geschafft! 🎉</Typography>
                                <Typography>Du hast alle verfügbaren Bücher bewertet.</Typography>
                                <Button onClick={() => loadBooks(true)} startIcon={<ReplayIcon />} sx={{ mt: 2 }}>
                                    Nach neuen Büchern suchen
                                </Button>
                                <Button onClick={() => navigate('/booklist')} startIcon={<ArrowBackIcon />} sx={{ mt: 2, ml: 1 }}>
                                    Zurück zur Liste
                                </Button>
                            </>
                        )}
                    </Paper>
                )}
            </Box>

            {/* Button-Bereich */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, gap: 1.5, opacity: canRate ? 1 : 0.5 }}>
                {/* Lesestatus Buttons */}
                {/* Die Logik hier funktioniert weiterhin, da readingDecision === '...' false ist, wenn readingDecision null ist */}
                <Stack direction="row" spacing={2}>
                    <Button
                        variant={readingDecision === 'wants_to_read' ? "contained" : "outlined"}
                        color="primary" size="small"
                        startIcon={readingDecision === 'wants_to_read' ? <RadioButtonCheckedIcon /> : <RadioButtonUncheckedIcon />}
                        onClick={() => setReadingDecision('wants_to_read')}
                        disabled={!canRate}
                    >
                        Will ich lesen
                    </Button>
                    <Button
                        variant={readingDecision === 'read' ? "contained" : "outlined"}
                        color="primary" size="small"
                        startIcon={readingDecision === 'read' ? <RadioButtonCheckedIcon /> : <RadioButtonUncheckedIcon />}
                        onClick={() => setReadingDecision('read')}
                        disabled={!canRate}
                    >
                        Gelesen
                    </Button>
                </Stack>
                {/* Keep/Discard Buttons */}
                <Stack direction="row" spacing={isMobile ? 3 : 4}>
                    <Button
                        variant="contained" color="error"
                        onClick={() => triggerSwipe('left')}
                        disabled={!canRate}
                        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 2, py: 1, textTransform: 'none' }}
                        aria-label="Kann weg"
                    >
                        <CancelIcon sx={{ mb: 0.5 }} />
                        <Typography variant="caption">Kann weg</Typography>
                    </Button>
                    <Button
                        variant="contained" color="success"
                        onClick={() => triggerSwipe('right')}
                        disabled={!canRate}
                        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 2, py: 1, textTransform: 'none' }}
                        aria-label="Behalten"
                    >
                        <CheckCircleIcon sx={{ mb: 0.5 }} />
                        <Typography variant="caption">Behalten</Typography>
                    </Button>
                </Stack>
            </Box>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default BookRater;

